// Diet marks on recipes: red for diets an ingredient certainly breaks, orange for warnings nobody
// confirmed (see logic/diets.ts). One symbol per diet.
import React from 'react';
import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { DIET_SYMBOLS, type DietFlags } from '../logic/diets';
import { radius, usePalette } from '../theme';
import type { Diet } from '../types';

export default function DietMark({ flags, size = 13 }: { flags: DietFlags; size?: number }) {
  const p = usePalette();
  if (!flags.breaks.length && !flags.unsure.length) return null;
  return (
    <View style={{ flexDirection: 'row', gap: 4, alignSelf: 'flex-start' }}>
      <DietPill diets={flags.breaks} color={p.danger} label="diet.breaks" size={size} />
      <DietPill diets={flags.unsure} color={p.warning} label="diet.mayNotFit" size={size} />
    </View>
  );
}

/** One pill of diet symbols in one colour. */
export function DietPill({ diets, color, label, size = 13 }: { diets: Diet[]; color: string; label?: 'diet.breaks' | 'diet.mayNotFit'; size?: number }) {
  const { t } = useTranslation();
  const p = usePalette();
  if (!diets.length) return null;
  const shown = diets.slice(0, 3);
  const text = { color: p.onTint, fontSize: size, lineHeight: size + 3, fontWeight: '700' as const };
  return (
    <View
      accessible
      accessibilityLabel={label ? t(label, { diets: diets.map(d => t(`diets.${d}`)).join(', ') }) : undefined}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: color, borderRadius: radius.pill, paddingHorizontal: 6, paddingVertical: 2 }}>
      {shown.map(d => {
        const symbol = DIET_SYMBOLS[d];
        return 'icon' in symbol ? <Icon key={d} name={symbol.icon} size={size} color={p.onTint} /> : <Text key={d} style={text}>{symbol.glyph}</Text>;
      })}
      {diets.length > shown.length ? <Text style={text}>+{diets.length - shown.length}</Text> : null}
    </View>
  );
}
